import React from 'react';

export interface PersianBackgroundProps {
  variant?: 'subtle' | 'elegant' | 'ornate' | 'minimal';
  opacity?: number;
  className?: string;
  children?: React.ReactNode;
  showFerdowsi?: boolean;
  showPattern?: boolean;
}

export const PersianBackground: React.FC<PersianBackgroundProps> = ({
  variant = 'elegant',
  opacity = 0.1,
  className = '',
  children,
  showFerdowsi = true,
  showPattern = true
}) => {
  const getBackgroundStyle = () => {
    switch (variant) {
      case 'subtle':
        return 'bg-gradient-to-br from-slate-50 via-blue-50/50 to-purple-50/50';
      case 'elegant':
        return 'bg-gradient-to-br from-slate-100 via-blue-100/60 to-purple-100/60';
      case 'ornate':
        return 'bg-gradient-to-br from-purple-100 via-blue-100 to-pink-100';
      case 'minimal':
        return 'bg-gradient-to-br from-gray-50 to-slate-50';
      default:
        return 'bg-gradient-to-br from-slate-100 via-blue-100/60 to-purple-100/60';
    }
  };

  const getPatternOpacity = () => {
    switch (variant) {
      case 'subtle':
        return 'opacity-10';
      case 'elegant':
        return 'opacity-15';
      case 'ornate':
        return 'opacity-25';
      case 'minimal':
        return 'opacity-8';
      default:
        return 'opacity-15';
    }
  };

  return (
    <div className={`relative min-h-screen ${getBackgroundStyle()} ${className}`}>
      {/* Ferdowsi statue watermark */}
      {showFerdowsi && (
        <div className={`absolute inset-0 ${getPatternOpacity()}`}>
          <div 
            className="absolute inset-0 bg-cover bg-center bg-no-repeat"
            style={{
              backgroundImage: 'url(/images/ferdowsi-statue.jpg), url(/images/placeholder-ferdowsi.svg)',
              backgroundSize: 'contain',
              backgroundPosition: 'center',
              backgroundRepeat: 'no-repeat',
              filter: 'grayscale(80%) contrast(1.2) brightness(0.9)',
              opacity: Math.max(opacity, 0.2) // Ensure minimum visibility
            }}
          ></div>
        </div>
      )}

      {/* Persian geometric patterns */}
      {showPattern && (
        <div className={`absolute inset-0 ${getPatternOpacity()}`}>
          {/* Subtle geometric pattern overlay */}
          <div className="absolute inset-0">
            <svg className="w-full h-full" xmlns="http://www.w3.org/2000/svg">
              <defs>
                <pattern id="persian-pattern" x="0" y="0" width="100" height="100" patternUnits="userSpaceOnUse">
                  <circle cx="50" cy="50" r="2" fill="currentColor" className="text-purple-300/30" />
                  <circle cx="25" cy="25" r="1" fill="currentColor" className="text-blue-300/30" />
                  <circle cx="75" cy="25" r="1" fill="currentColor" className="text-pink-300/30" />
                  <circle cx="25" cy="75" r="1" fill="currentColor" className="text-green-300/30" />
                  <circle cx="75" cy="75" r="1" fill="currentColor" className="text-indigo-300/30" />
                </pattern>
              </defs>
              <rect width="100%" height="100%" fill="url(#persian-pattern)" />
            </svg>
          </div>
        </div>
      )}

      {/* Persian calligraphy watermarks */}
      {variant === 'ornate' && (
        <div className="absolute inset-0 overflow-hidden pointer-events-none">
          <div className="absolute top-10 right-10 text-6xl font-persian text-purple-200/20 transform rotate-12">
            فارسی
          </div>
          <div className="absolute bottom-10 left-10 text-4xl font-persian text-blue-200/20 transform -rotate-12">
            شاهنامه
          </div>
          <div className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 text-8xl font-persian text-pink-200/10 rotate-45">
            فردوسی
          </div>
        </div>
      )}

      {/* Content */}
      <div className="relative z-10">
        {children}
      </div>
    </div>
  );
};

export default PersianBackground;